import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { StyledInstanceCreateDateText } from './styles';
import { CLUSTER_TYPES } from '../../../constants';
import { useUser } from '../../../contexts/UserContext';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import { getInitials, isGPSInstance } from '../../../utils/utils';
import Image from '../../elements/image/Image';
import Status from '../../elements/status/Status';
import Text from '../../elements/text/Text';
import SecondaryChip from '../chips/SecondaryChip';

const InstanceListItem = ({ instance }) => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { user } = useUser();

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const pathInfo = {
    [CLUSTER_TYPES.GPS_CLUSTER]: {
      path: PRIVATE_ROUTE.GPS_OVERVIEW,
      params: { projectId, gpsId: instance.id },
    },
    [CLUSTER_TYPES.MODEL_CLUSTER]: {
      path: PRIVATE_ROUTE.MODEL_OVERVIEW,
      params: { projectId, modelId: instance.id },
    },
    [CLUSTER_TYPES.CUSTOM_CLUSTER]: {
      path: PRIVATE_ROUTE.CUSTOM_INST_OVERVIEW,
      params: { projectId, customInstId: instance.id },
    },
    [CLUSTER_TYPES.ASSISTANT_CLUSTER]: {
      path: PRIVATE_ROUTE.ASSISTANT_OVERVIEW,
      params: { projectId, assistantId: instance.id },
    },
  };

  const secondaryAction = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {instance.createDate && (
        <Box sx={{ ml: 1 }}>
          <Text caption>
            {t('createdAt', {
              ns: 'instance',
              date: formatDateTimeUTC(instance.createDate, user?.timeZone),
            })}
          </Text>
        </Box>
      )}
    </Box>
  );

  return (
    <ListItem secondaryAction={!isSmallScreen && secondaryAction}>
      <ListItemAvatar>
        <Image
          size="small"
          shape="rounded"
          alt={instance.name}
          src={instance?.icon}
        >
          {getInitials(instance.name)}
        </Image>
      </ListItemAvatar>

      <ListItemText>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link
            component={RouterLink}
            to={buildPath({ ...pathInfo[instance.clusterType] })}
            color="inherit"
            underline="hover"
          >
            {instance.name}
          </Link>

          <Status instanceState={instance.status} solrRunning={true} />
          {!isGPSInstance(instance.clusterType) && (
            <Box sx={{ ml: 2 }}>
              <SecondaryChip instance={instance} />
            </Box>
          )}
        </Box>
        {isSmallScreen && (
          <StyledInstanceCreateDateText caption>
            {t('createdAt', {
              ns: 'instance',
              date: formatDateTimeUTC(instance.createDate, user?.timeZone),
            })}
          </StyledInstanceCreateDateText>
        )}
      </ListItemText>
    </ListItem>
  );
};

InstanceListItem.propTypes = {
  instance: PropTypes.object.isRequired,
};

export default InstanceListItem;
